import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity, 
  Zap, 
  Users, 
  Shield, 
  Coins,
  Globe,
  TrendingUp,
  Settings,
  Battery,
  Lightbulb,
  BarChart3,
  Network,
  Send,
  Gift,
  Server,
  UserCheck,
  ChevronDown,
  ChevronRight,
  Calculator,
  Wallet,
  Award,
  CreditCard,
  History,
  Star,
  Target,
  Layers,
  Database,
  Lock,
  Unlock,
  DollarSign,
  PieChart,
  FileText,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  RefreshCw,
  User,
  LogOut,
  LogIn,
  Sparkles,
  Percent
} from 'lucide-react';
import { useScanLanguage } from '../contexts/LanguageContext';

const Sidebar: React.FC = () => {
  const { t } = useScanLanguage();
  const location = useLocation();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [userBalance, setUserBalance] = useState({ vttn: 0, usdt: 0 });
  const [userTier, setUserTier] = useState('Bronze');

  // 지갑 연결 상태 확인
  useEffect(() => {
    const checkWalletConnection = () => {
      const savedAddress = localStorage.getItem('walletAddress');
      const savedBalance = localStorage.getItem('userBalance');
      const savedTier = localStorage.getItem('userTier');
      
      if (savedAddress) {
        setIsWalletConnected(true);
        setWalletAddress(savedAddress);
        if (savedBalance) {
          setUserBalance(JSON.parse(savedBalance));
        }
        if (savedTier) {
          setUserTier(savedTier);
        }
      }
    };
    
    checkWalletConnection();
    
    // 지갑 상태 변경 이벤트 리스너
    window.addEventListener('walletStateChanged', checkWalletConnection);
    
    return () => {
      window.removeEventListener('walletStateChanged', checkWalletConnection);
    };
  }, []);

  // 지갑 연결 함수
  const connectWallet = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (accounts.length > 0) {
          const address = accounts[0];
          setWalletAddress(address);
          setIsWalletConnected(true);
          localStorage.setItem('walletAddress', address);
          
          // 사용자 정보 가져오기
          await fetchUserInfo(address);
          
          // 지갑 상태 변경 이벤트 발생
          window.dispatchEvent(new Event('walletStateChanged'));
        }
      } else {
        alert(t('wallet.metamask'));
      }
    } catch (error) {
      console.error('지갑 연결 실패:', error);
    }
  };

  // 지갑 연결 해제
  const disconnectWallet = () => {
    setIsWalletConnected(false);
    setWalletAddress('');
    setUserBalance({ vttn: 0, usdt: 0 });
    setUserTier('Bronze');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('userBalance');
    localStorage.removeItem('userTier');
    window.dispatchEvent(new Event('walletStateChanged'));
  };

  // 사용자 정보 가져오기
  const fetchUserInfo = async (address: string) => {
    try {
      const response = await fetch(`http://localhost:3600/api/wallet/balance/${address}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const balance = {
            vttn: data.data.vttn_balance || 0,
            usdt: data.data.usdt_balance || 0
          };
          setUserBalance(balance);
          setUserTier(data.data.tier || 'Bronze');
          localStorage.setItem('userBalance', JSON.stringify(balance));
          localStorage.setItem('userTier', data.data.tier || 'Bronze');
        }
      }
    } catch (error) {
      console.error('사용자 정보 가져오기 실패:', error);
    }
  };

  // 주소 축약 함수
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const menuItems = [
    { name: t('menu.dashboard'), href: '/', icon: BarChart3, key: 'dashboard' },
    { name: t('menu.blocks'), href: '/blocks', icon: Activity, key: 'blocks' },
    { name: t('menu.transactions'), href: '/transactions', icon: Zap, key: 'transactions' },
    { name: t('menu.addresses'), href: '/addresses', icon: Users, key: 'addresses' },
    { name: t('menu.tokens'), href: '/tokens', icon: Coins, key: 'tokens' },
    { name: t('menu.validators'), href: '/validators', icon: Shield, key: 'validators' },
    { name: t('menu.subnets'), href: '/subnets', icon: Network, key: 'subnets' },
    { name: t('menu.governance'), href: '/governance', icon: Users, key: 'governance' },
    { name: t('menu.network'), href: '/network', icon: Network, key: 'network' },
    { name: t('menu.energyBss'), href: '/energy-bss', icon: Battery, key: 'energyBss' },
    { name: t('menu.bridge'), href: '/bridge', icon: Layers, key: 'bridge' },
  ];

  const adminMenuItems = [
    { name: t('admin.dashboard'), href: '/admin', icon: BarChart3 },
    { name: t('admin.token'), href: '/admin/voltron-token', icon: Coins },
    { name: t('admin.bonus'), href: '/admin/bonus-management', icon: Gift },
    { name: t('admin.walletMgmt'), href: '/admin/wallet-management', icon: Wallet },
    { name: t('admin.discount'), href: '/admin/discount-management', icon: Percent },
    { name: t('admin.wallet'), href: '/wallet', icon: Wallet },
    { name: t('admin.users'), href: '/admin/user-management', icon: UserCheck },
    { name: t('admin.staking'), href: '/admin/staking', icon: TrendingUp },
    { name: t('admin.nodeSub'), href: '/admin/node-subscription', icon: Server },
    { name: t('admin.tokenTransfer'), href: '/admin/token-transfer', icon: Send },
    { name: t('admin.minting'), href: '/admin/token-minting', icon: Sparkles },
    { name: t('admin.boom'), href: '/admin/boom-events', icon: Target },
    { name: t('admin.referrals'), href: '/admin/referrals', icon: Star },
    { name: t('admin.referralMgmt'), href: '/admin/referral-management', icon: Users },
    { name: t('admin.serviceMgmt'), href: '/admin/service-management', icon: Settings },
    { name: t('admin.settlement'), href: '/admin/settlement', icon: Calculator },
    { name: t('admin.foundation'), href: '/admin/foundation-wallet', icon: Wallet },
    { name: t('admin.logs'), href: '/admin/logs', icon: FileText },
    { name: t('admin.security'), href: '/admin/security', icon: Lock },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      {/* 로고 영역 */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
            <Battery className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Voltron</h1>
            
          </div>
        </div>
      </div>

      {/* 지갑 연결 상태 */}
      <div className="p-4 border-b border-gray-200">
        {isWalletConnected ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-800">{t('sidebar.connected')}</span>
              </div>
              <button
                onClick={disconnectWallet}
                className="text-xs text-green-600 hover:text-green-800"
              >
                <LogOut className="w-3 h-3" />
              </button>
            </div>
            <div className="text-xs text-green-700 mb-2">
              {formatAddress(walletAddress)}
            </div>
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-green-600">VTTN:</span>
                <span className="font-medium text-green-800">{userBalance.vttn.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-600">USDT:</span>
                <span className="font-medium text-green-800">{userBalance.usdt.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-600">Tier:</span>
                <span className="font-medium text-green-800">{userTier}</span>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={connectWallet}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg p-3 flex items-center justify-center space-x-2 transition-colors"
          >
            <LogIn className="w-4 h-4" />
            <span className="text-sm font-medium">{t('sidebar.connectWallet')}</span>
          </button>
        )}
      </div>

      {/* 네트워크 상태 */}
      <div className="p-4 border-b border-gray-200">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-800">{t('sidebar.chainId')}</span>
          </div>
          <div className="mt-1 text-xs text-green-600">
            {t('sidebar.network')}
          </div>
        </div>
      </div>

      {/* 메뉴 항목들 */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <li key={item.key}>
                <Link
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-orange-100 text-orange-900 border border-orange-200'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${
                    active ? 'text-orange-600' : 'text-gray-500'
                  }`} />
                  <span>{item.name}</span>
                </Link>
              </li>
            );
          })}
          
          {/* 관리자 메뉴 드롭다운 */}
          <li>
            <button
              onClick={() => setIsAdminOpen(!isAdminOpen)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <Settings className="w-5 h-5 text-gray-500" />
                <span>{t('admin.menu')}</span>
              </div>
              {isAdminOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
            
            {isAdminOpen && (
              <ul className="mt-1 ml-8 space-y-1">
                {adminMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'bg-orange-100 text-orange-900 border border-orange-200'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${
                          active ? 'text-orange-600' : 'text-gray-400'
                        }`} />
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        </ul>
      </nav>

      {/* 하단 정보 */}
      <div className="p-4 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 space-y-1">
            <div className="flex justify-between">
              <span>{t('sidebar.vttnPrice')}</span>
              <span className="font-medium text-green-600">$0.5</span>
            </div>
            <div className="flex justify-between">
              <span>{t('sidebar.tps')}</span>
              <span className="font-medium">1,247</span>
            </div>
            <div className="flex justify-between">
              <span>{t('sidebar.validators')}</span>
              <span className="font-medium">4 {t('sidebar.validatorsActive')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;